import React, { useEffect, useRef } from 'react';
import { useImmer } from 'use-immer';
import { SelectBoxComponent } from '@/components';
import { cn } from '@/utils';
import AccordionModal from "../AccordionModal";

const Finishing = ({
  parentId,
  headline,
  onItemClick,
  loadingIndex,
  isFetching,
  values,
  previewIconsPath,
  toggleDropdown,
  closeDropdown,
  selection_id,
  selection_label,
  selection_sub_label,
  isExpanded
}) => {
  const PREVIEW_ICONS_PREFIX = '/finishing/';
  const PREVIEW_ICONS_SUFFIX = '_preview';
  const PREVIEW_ICONS_EXTENSION = '.png';
  const ID = 'finishing';
  const selectBoxRef = useRef();

  const [componentState, updateComponentState] = useImmer({
    isModalOpen: false,
    activeAccordionIndex: values.findIndex((value) => value.is_selected),
  });

  const openModal = () => {
    updateComponentState((draft) => {
      draft.isModalOpen = true;
    });
  };

  const closeModal = () => {
    updateComponentState((draft) => {
      draft.isModalOpen = false;
    });
  };

  useEffect(() => {
    document.body.style.overflow = componentState.isModalOpen ? 'hidden' : '';
  }, [componentState.isModalOpen]);

  useEffect(() => {
    if (!loadingIndex && componentState.isModalOpen) {
      closeModal();
    }
  }, [loadingIndex]);

  const getPreviewImagePath = (id) => {
    return (
      previewIconsPath +
      PREVIEW_ICONS_PREFIX +
      id +
      PREVIEW_ICONS_SUFFIX +
      PREVIEW_ICONS_EXTENSION
    );
  };

  const handleItemClick = ({ parentId, id, is_selected }, i) => {
    onItemClick({ parentId, id, is_selected });
    updateComponentState((draft) => {
      draft.activeAccordionIndex = i;
    });
  };

  const selectTooltip = (i) => {
    updateComponentState((draft) => {
      draft.activeAccordionIndex = i;
    });
  };

  useEffect(() => {
    updateComponentState((draft) => {
      draft.activeAccordionIndex = values.findIndex(value => value.is_selected);
    });
  }, [values]);

  return (
    <>
      <SelectBoxComponent
        id={ID}
        ref={selectBoxRef}
        testid = {process.env.RENDER_TESTIDS ? "finishingselect" : undefined}
        showIcon={values.length > 1}
        onClick={() => { if (values.length > 1) { toggleDropdown(); }}}
        onClickOutside={() => closeDropdown()}
        onKeyDown={(e) => {
          // Handle Enter, Space and Escape key
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleDropdown();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            closeDropdown();
          }
        }}
        linkText={values.every(value => value.tooltip && value.tooltip.trim() !== '') ? Drupal.t('Compare Varnishes') : false}
        onLinkClick={() => openModal()}
        isDisabled={isFetching}
        disabledClassName="opacity-40 bg-neutral-light"
        buttonDataAttributes={{'data-finishing': selection_id}}
        {...{ isExpanded, headline }}
        DropdownContent={
          <div className="absolute z-overlay mt-3xs w-full bg-white shadow border border-neutral-darker rounded overflow-hidden">
            <ul
              data-testid={process.env.RENDER_TESTIDS ? "finishingdropdown" : undefined}
              role="listbox"
              aria-labelledby={`listbox-${ID}-label`}
              className={cn(
                "dropdown-content overflow-auto m-0 max-h-[50vh] laptop:max-h-[40vh]",
                isFetching ? "opacity-60" : ""
              )}
            >
              {values.map(({label, sub_label, is_selected, id}, i) => (
                <li
                  data-testid={id}
                  key={i}
                  tabIndex="0"
                  id={`listbox-${ID}-${i}`}
                  role="option"
                  aria-selected={is_selected}
                  data-paper={id}
                  className={cn(
                    'relative flex flex-row items-center py-sm px-lg border-b border-b-neutral-darker cursor-pointer',
                    'hover:bg-neutral-light focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-1',
                    is_selected || loadingIndex === `${parentId}_${id}`
                      ? 'bg-neutral-light ring-2 ring-inset ring-brand-1 border-b-0'
                      : 'bg-white'
                  )}
                  onClick={() =>
                    handleItemClick({parentId, id, is_selected}, i)
                  }
                  onKeyDown={(e) => {
                    // Handle Enter, Space and Escape key
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleItemClick({parentId, id, is_selected}, i);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      closeDropdown();
                    }
                  }}
                  onBlur={({ relatedTarget }) => {
                    if (relatedTarget && selectBoxRef.current && !selectBoxRef.current.contains(relatedTarget)) {
                      closeDropdown();
                    }
                  }}
                >
                  <div className="flex-shrink-0 w-6xl h-2xl mr-lg">
                    <img
                      alt=""
                      className="w-full"
                      src={getPreviewImagePath(id)}
                      width="60"
                      height="30"
                    />
                  </div>
                  <div className="flex flex-col leading-5">
                    <span className="font-semibold">{label}</span>
                    <If condition={sub_label}>
                      <span className="font-normal text-xs">
                        {sub_label}
                      </span>
                    </If>
                  </div>
                  <If condition={loadingIndex === `${parentId}_${id}`}>
                    <div className="absolute inset-y-0 right-0 pr-lg flex items-center">
                      <ft-spinner size="small"></ft-spinner>
                    </div>
                  </If>
                </li>
              ))}
            </ul>
          </div>
        }
      >
        <div className="flex items-center">
          <div
            className="flex-shrink-0 w-6xl h-2xl mr-lg">
            <img
              className="w-full"
              src={getPreviewImagePath(
                values.find((value) => value.is_selected).id,
              )}
              alt=""
              width="60"
              height="30"
            />
          </div>
          <div className="flex flex-col items-start text-left leading-5">
            {selection_label}
            <If condition={selection_sub_label}>
              <span className="font-normal text-xs">{selection_sub_label}</span>
            </If>
          </div>
        </div>
      </SelectBoxComponent>

      <If condition={componentState.isModalOpen}>
        <AccordionModal
          headline={Drupal.t('Select Varnish')}
          ctaLabel={Drupal.t('Select Varnish')}
          closeModal={() => closeModal()}
          values={values}
          getPreviewImagePath={getPreviewImagePath}
          activeAccordionIndex={componentState.activeAccordionIndex}
          selectTooltip={selectTooltip}
          parentId={parentId}
          onItemClick={onItemClick}
          loadingIndex={loadingIndex}
        />
      </If>
    </>
  );
};

export default Finishing;
